// tippi/src/pages/admin/invoices/TippiBillingForm.js
import React, { useState } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { useTippiBilling } from '../../../hooks/useTippiBilling';
import {
  EMPTY_INVOICE_PROFILE, normalizeInvoiceProfile, validateInvoiceProfile, isInvoiceProfileComplete,
} from '../../../utils/invoiceProfile';
import { COLORS } from '../../../styles/tokens';

// Tippis egne fakturaopplysninger (settings/tippiBilling). Gebyrfakturaer lages ikke uten dem.
export default function TippiBillingForm() {
  const { billing, loading, error } = useTippiBilling();
  if (loading) {
    return <section style={styles.card} aria-label="Tippis fakturaopplysninger"><p style={styles.hint}>Laster…</p></section>;
  }
  return <BillingFields billing={billing} readError={!!error} />;
}

function BillingFields({ billing, readError }) {
  const [form, setForm] = useState(() => ({
    ...EMPTY_INVOICE_PROFILE, ...(billing ?? {}),
    invoiceAddress: { ...EMPTY_INVOICE_PROFILE.invoiceAddress, ...(billing?.invoiceAddress ?? {}) },
  }));
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'

  const edited = () => setStatus(s => (s === 'saving' ? s : 'idle'));
  const set = (field) => (e) => { edited(); setForm(f => ({ ...f, [field]: e.target.value })); };
  const setAddr = (field) => (e) => { edited(); setForm(f => ({ ...f, invoiceAddress: { ...f.invoiceAddress, [field]: e.target.value } })); };

  async function handleSave() {
    if (status === 'saving' || readError) return;
    const found = validateInvoiceProfile(form);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setStatus('saving');
    try {
      // Bare de seks feltene, uten merge
      await setDoc(doc(db, 'settings', 'tippiBilling'), normalizeInvoiceProfile(form));
      setStatus('saved');
    } catch (err) {
      console.error('TippiBillingForm:', err);
      setStatus('error');
    }
  }

  const complete = isInvoiceProfileComplete(billing);

  return (
    <section style={styles.card} aria-label="Tippis fakturaopplysninger">
      <h2 style={styles.title}>Tippis fakturaopplysninger</h2>
      {readError && (
        <p role="alert" style={styles.error}>Kunne ikke lese lagrede opplysninger. Lagring er slått av for å unngå å overskrive dem. Last siden på nytt.</p>
      )}
      {!readError && !complete && (
        <p role="status" style={styles.warn}>Gebyrfakturaer opprettes ikke før opplysningene er fylt ut</p>
      )}
      <Field id="tb-name" label="Juridisk navn" value={form.legalName} onChange={set('legalName')} error={errors.legalName} maxLength={120} />
      <Field id="tb-org" label="Organisasjonsnummer" value={form.orgNumber} onChange={set('orgNumber')} error={errors.orgNumber} inputMode="numeric" />
      <label style={styles.check}>
        <input type="checkbox" checked={form.mvaRegistered === true}
               onChange={e => { edited(); setForm(f => ({ ...f, mvaRegistered: e.target.checked })); }} />
        <span>Tippi er registrert i mva-registeret</span>
      </label>
      <Field id="tb-account" label="Kontonummer" value={form.bankAccount} onChange={set('bankAccount')} error={errors.bankAccount} inputMode="numeric" />
      <Field id="tb-street" label="Gateadresse" value={form.invoiceAddress.street} onChange={setAddr('street')} error={errors.street} maxLength={120} />
      <div style={styles.row}>
        <Field id="tb-postal" label="Postnummer" value={form.invoiceAddress.postalCode} onChange={setAddr('postalCode')} error={errors.postalCode} inputMode="numeric" maxLength={4} style={{ flex: '0 0 110px' }} />
        <Field id="tb-city" label="Poststed" value={form.invoiceAddress.city} onChange={setAddr('city')} error={errors.city} maxLength={80} style={{ flex: 1 }} />
      </div>
      {status === 'error' && <p role="alert" style={styles.error}>Kunne ikke lagre. Prøv igjen.</p>}
      {status === 'saved' && <p role="status" style={styles.saved}>Lagret</p>}
      <button type="button" style={styles.save} onClick={handleSave} disabled={status === 'saving' || readError}>
        {status === 'saving' ? 'Lagrer…' : 'Lagre'}
      </button>
    </section>
  );
}

function Field({ id, label, value, onChange, error, inputMode, maxLength, style }) {
  return (
    <div style={{ marginTop: 10, ...style }}>
      <label htmlFor={id} style={styles.label}>{label}</label>
      <input id={id} value={value} onChange={onChange} inputMode={inputMode}
             maxLength={maxLength} autoComplete="off"
             aria-invalid={!!error} aria-describedby={error ? id + '-err' : undefined}
             style={{ ...styles.input, ...(error ? styles.inputError : {}) }} />
      {error && <div id={id + '-err'} role="alert" style={styles.fieldError}>{error}</div>}
    </div>
  );
}

const styles = {
  card:       { background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: 16, marginBottom: 16 },
  title:      { fontSize: 17, fontWeight: 700, margin: 0, color: COLORS.textDark },
  hint:       { fontSize: 12.5, color: COLORS.textMid, margin: 0 },
  warn:       { background: COLORS.warningBg, color: COLORS.warningText, padding: '8px 12px', borderRadius: 8, fontSize: 13.5, margin: '10px 0 0' },
  label:      { display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.textMid, marginBottom: 5 },
  input:      { width: '100%', boxSizing: 'border-box', height: 42, padding: '0 12px', borderRadius: 10, border: `1px solid ${COLORS.border}`, fontSize: 14.5, fontFamily: 'inherit', background: COLORS.white, color: COLORS.textDark },
  inputError: { borderColor: COLORS.danger },
  fieldError: { fontSize: 12, color: COLORS.dangerText, marginTop: 4 },
  check:      { display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, marginTop: 12, color: COLORS.textDark },
  row:        { display: 'flex', gap: 10 },
  error:      { background: COLORS.dangerBg, color: COLORS.dangerText, padding: '8px 12px', borderRadius: 8, fontSize: 13.5, margin: '10px 0 0' },
  saved:      { color: COLORS.successText, fontSize: 14, fontWeight: 600, margin: '10px 0 0' },
  save:       { background: COLORS.accent, color: '#1a1a2e', border: 'none', borderRadius: 20, padding: '10px 22px', fontWeight: 700, fontSize: 14, cursor: 'pointer', marginTop: 14 },
};
